import { MathJax } from 'better-react-mathjax';
import { memo, useMemo } from 'react';
import { sanitizeHtml } from 'utils/htmlUtils';
import { isString } from 'utils/stringUtils';

// Regex compile 1 lần (tối ưu performance)
const FIX_MI_EMPTY = /<mi>\s*&amp;\s*<\/mi>/g;
const FIX_MI_PREFIX = /<mi>\s*&amp;([^<]+)<\/mi>/g;
const FIX_MTD_PREFIX = /(<mtd[^>]*>)\s*&amp;\s*/g;

// LaTeX artifacts cleanup regex
const LATEX_CLOSE_PAREN_BRACE = /\\\)\}/g;
const LATEX_ESCAPED_AMP = /\\&/g;
const LATEX_FIGURE_BLOCK = /\\begin\{figure\}[\s\S]*?\\end\{figure\}/gi;
const LATEX_FIGURE_COMMAND = /\\figure\b/gi;

/**
 * Clean HTML content - remove empty paragraphs, extra whitespace, etc.
 * Optimized version with combined regex and minimal string copies
 * @param {string} html
 * @returns {string}
 */
function cleanHtmlContent(html) {
  if (!html) return '';

  // Convert to string once
  let cleaned = html.toString().trim();

  // Early return if already clean
  if (!cleaned.includes('<p>') && !cleaned.includes('<br')) {
    return cleaned;
  }

  // Combine all replacements to minimize string copies
  // Order matters: remove empty tags first, then trim leading/trailing br
  cleaned = cleaned
    // Remove all empty paragraphs (with &nbsp; or empty)
    .replace(/<p>\s*(?:&nbsp;)?\s*<\/p>/gi, '')
    // Trim to expose leading/trailing br
    .trim()
    // Remove leading <br> tags
    .replace(/^(?:<br\s*\/?>)+/gi, '')
    // Remove trailing <br> tags
    .replace(/(?:<br\s*\/?>)+$/gi, '')
    // Final trim in case br removal left whitespace
    .trim();

  return cleaned;
}

/**
 * Convert LaTeX text commands to HTML
 * \emph{text} → <em>text</em>
 * \textbf{text} → <strong>text</strong>
 * @param {string} text
 * @returns {string}
 */
function convertLatexTextCommands(text) {
  if (!text) return '';

  let result = text;

  // \textbf{...} → <strong>...</strong>
  result = result.replace(/\\textbf\{([^}]+)\}/g, '<strong>$1</strong>');

  // \emph{...} → <em>...</em>
  result = result.replace(/\\emph\{([^}]+)\}/g, '<em>$1</em>');

  // \textit{...} → <em>...</em>
  result = result.replace(/\\textit\{([^}]+)\}/g, '<em>$1</em>');

  // \underline{...} → <u>...</u>
  result = result.replace(/\\underline\{([^}]+)\}/g, '<u>$1</u>');

  return result;
}

/**
 * Fix MathML bị dư ký tự &
 * CKEditor/MathType sinh ra <mi>&amp;</mi> hoặc <mi>&amp;x</mi>
 * @param {string} html
 * @returns {string}
 */
function fixBrokenMathML(html) {
  if (!/<math[\s>]/i.test(html)) return html;

  return html
    .replace(FIX_MI_EMPTY, '')
    .replace(FIX_MI_PREFIX, '<mi>$1</mi>')
    .replace(FIX_MTD_PREFIX, '$1');
}

/**
 * Clean LaTeX artifacts - remove malformed LaTeX syntax
 * Xử lý các trường hợp LaTeX bị lỗi từ editor hoặc conversion
 * @param {string} text
 * @returns {string}
 */
function cleanLatexArtifacts(text) {
  if (!text) return '';

  return (
    text
      // Remove \)} - malformed LaTeX closing
      .replace(LATEX_CLOSE_PAREN_BRACE, '')
      // Convert \& to & (escaped ampersand)
      .replace(LATEX_ESCAPED_AMP, '&')
      // Remove \begin{figure}...\end{figure} blocks
      .replace(LATEX_FIGURE_BLOCK, '')
      // Remove standalone \figure command
      .replace(LATEX_FIGURE_COMMAND, '')
  );
}

/**
 * Math Renderer - Optimized for multiple instances
 * - Clean HTML (remove empty paragraphs, extra whitespace)
 * - Clean LaTeX artifacts (malformed LaTeX syntax)
 * - Convert LaTeX text commands (\textbf, \emph)
 * - Fix MathML lỗi từ CKEditor (ký tự & dư)
 * - MathJax tự động xử lý LaTeX ở App level
 * - Global MutationObserver (ở App.js) tự động convert tables
 *
 * @param {object} props
 * @param {string} props.html
 * @returns
 */
function MathRenderer({ html }) {
  const processedHtml = useMemo(() => {
    if (!isString(html)) return '';

    // 1. Clean HTML content first
    const cleaned = cleanHtmlContent(html);

    // 2. Clean LaTeX artifacts
    const latexCleaned = cleanLatexArtifacts(cleaned);

    // 3. Sanitize with DOMPurify (security + MathML support)
    const sanitized = sanitizeHtml(latexCleaned);

    // 4. Convert LaTeX text commands to HTML
    const withLatexCommands = convertLatexTextCommands(sanitized);

    // 5. Fix broken MathML
    return fixBrokenMathML(withLatexCommands);
  }, [html]);

  return (
    <MathJax>
      <div dangerouslySetInnerHTML={{ __html: processedHtml }} />
    </MathJax>
  );
}

export default MathRenderer;
export const MemoMathRenderer = memo(MathRenderer);
